import { useState, type MouseEvent } from 'react';
import { IconButton } from '@mui/material';
import { DeleteOutlined } from '@mui/icons-material';
import { useDeleteSessionMutation } from '@/state/mutations/sessions/use-delete-session.ts';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useNotifications } from '@/hooks/use-notifications';
import ConfirmationModal from '@/components/common/confirmation-modal.tsx';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';

export const SessionDelete = ({ session }: { session: SessionDto }) => {
  const t = useTranslations();
  const notification = useNotifications();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { mutateAsync: deleteSessionMutation, isPending: isDeletingPending } = useDeleteSessionMutation();
  const mode = useEventMode();

  const handleDeleteIconClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteModalCancel = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteSession = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    try {
      await deleteSessionMutation({ sessionId: session.id });
      notification.notifySuccess(t('session.notification.deleteSuccess', { title: session.title }));
      setIsDeleteDialogOpen(false);
    } catch {
      notification.notifyError(t('session.notification.deleteError'));
    }
  };

  return (
    <>
      <IconButton
        aria-label="delete"
        onClick={handleDeleteIconClick}
        loading={isDeletingPending}
        disabled={isOrganizeMode(mode)}
        size="small"
        sx={{
          width: '32px',
          height: '32px',
        }}
      >
        <DeleteOutlined fontSize="small" />
      </IconButton>
      <ConfirmationModal
        open={isDeleteDialogOpen}
        isLoading={isDeletingPending}
        title={t('session.deleteModal.title')}
        text={t('session.deleteModal.text', { title: session.title })}
        onConfirm={handleDeleteSession}
        onCancel={handleDeleteModalCancel}
      />
    </>
  );
};
